import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as MenuIcon } from '../../assets/icon/menu.svg';
import { ReactComponent as OrderHistoryIcon } from '../../assets/icon/order-history.svg';
import { ReactComponent as SeatIcon } from '../../assets/icon/seat.svg';
import { TABS } from '../../consts';
import { useSystemContext } from '../../context/SystemContext/SystemContext';
import { useUserMenuContext } from '../../context/UserMenuContext';
import SeatInfoTab from '../SeatInfo/SeatInfoTab';
import HomeTabNew from './components/HomeTabV2/HomeTabNew';
import OrderHistoryTab from './components/OrderHistoryTab/OrderHistoryTab';

const NewOrderV2: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setActiveTab, activeTab } = useUserMenuContext();
  const { stadiumInfo, seatInfo } = useSystemContext();

  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');

  const handleTabChange = (tab: TABS) => {
    if (tab === TABS.HOME) {
      const currentParams = new URLSearchParams(searchParams);
      currentParams.delete('tab');
      navigate(`?${currentParams.toString()}`);
    }
    setActiveTab(tab);
  };

  useEffect(() => {
    if (tab && Object.values(TABS).includes(tab as TABS)) {
      setActiveTab(tab as TABS);
    } else {
      setActiveTab(TABS.HOME);
    }
  }, [tab]);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef<Record<string, number>>({});

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = scrollPositions.current[activeTab] ?? 0;
      }
    });

    return () => {
      if (container) scrollPositions.current[activeTab] = container.scrollTop;
    };
  }, [activeTab]);

  useEffect(() => {
    if (seatInfo?.id) {
      scrollPositions.current = {};
      requestAnimationFrame(() => {
        if (contentRef.current) contentRef.current.scrollTop = 0;
      });
    }
  }, [seatInfo?.id]);

  const onStartOrder = () => {
    setActiveTab(TABS.HOME);
  };

  const renderTabContent = () => {
    if (!seatInfo?.id) {
      return <SeatInfoTab setActiveTab={setActiveTab} />;
    }
    switch (activeTab) {
      case TABS.HOME:
        return <HomeTabNew />;
      case TABS.ORDER_HISTORY:
        return <OrderHistoryTab onStartOrder={onStartOrder} />;
      case TABS.SEAT:
        return <SeatInfoTab setActiveTab={setActiveTab} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col items-center bg-black-0" style={{ height: '100dvh' }}>
      <div ref={contentRef} className="relative flex-1 min-h-0 overflow-auto w-full max-w-2xl bg-white">{renderTabContent()}</div>
      <div className="flex flex-col justify-center items-end max-w-2xl w-full">
        <div className="flex w-full bg-white flex-grow relative">
          <div
            className="h-1 bg-green-500 transition-transform duration-300 left-0 top-[-2px] absolute"
            style={{
              width: '33.33%',
              transform: activeTab === TABS.SEAT ? 'translateX(0)' : activeTab === TABS.HOME ? 'translateX(100%)' : 'translateX(200%)',
            }}
          />
          <button
            className={`w-1/3 ${activeTab === TABS.SEAT ? 'text-green-500 font-semibold' : 'text-gray-400 font-medium'
              } flex flex-col items-center text-sm leading-5 px-2 py-10`}
            onClick={() => handleTabChange(TABS.SEAT)}
          >
            <SeatIcon width={24} height={24} />
            {t('SEAT')}
          </button>
          <button
            className={`w-1/3 ${activeTab === TABS.HOME ? 'text-green-500 font-semibold' : 'text-gray-400 font-medium'
              } flex flex-col items-center text-sm leading-5 px-2 py-10 disabled:opacity-25 disabled:cursor-not-allowed`}
            onClick={() => handleTabChange(TABS.HOME)}
            disabled={!seatInfo?.id || !seatInfo?.isConfirmed || !stadiumInfo?.id}
          >
            <MenuIcon width={24} height={24} />
            {t('HOME_TAB_MENU')}
          </button>
          <button
            className={`w-1/3 ${activeTab === TABS.ORDER_HISTORY ? 'text-green-500 font-semibold' : 'text-gray-400 font-medium'
              } flex flex-col items-center text-sm leading-5 px-2 py-10 disabled:opacity-25 disabled:cursor-not-allowed`}
            onClick={() => handleTabChange(TABS.ORDER_HISTORY)}
            disabled={!seatInfo?.id || !seatInfo?.isConfirmed || !stadiumInfo?.id}
          >
            <OrderHistoryIcon width={24} height={24} />
            {t('HOME_TAB_ORDERHISTORY')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewOrderV2;
